import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, Download, FileText, AlertCircle, Building2, Calendar } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function PublicReportView() {
    const { reportId } = useParams();
    const [meta, setMeta] = useState(null);
    const [pin, setPin] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/reports/${reportId}/meta`).then((r) => setMeta(r.data)).catch(() => setError("Reporte no encontrado"));
    }, [reportId]);

    const submit = async (val) => {
        const p = (val || pin).trim();
        if (p.length !== 4) return;
        setLoading(true);
        try {
            const r = await api.post(`/public/reports/${reportId}/verify-pin`, { pin: p });
            setData(r.data);
        } catch (e) {
            toast.error("PIN incorrecto");
        } finally { setLoading(false); }
    };

    if (error) return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <Card className="max-w-md p-6 text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <h2 className="text-lg font-semibold">{error}</h2>
            </Card>
        </div>
    );

    if (!data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
                <div className="mb-6"><BrandLogo size={42} /></div>
                <Card className="max-w-md w-full p-6">
                    <div className="text-center mb-4">
                        <div className="inline-flex w-12 h-12 rounded-full bg-[hsl(var(--accent))]/20 items-center justify-center mb-2">
                            <Lock className="w-6 h-6 text-[hsl(var(--trulyn-navy))]" />
                        </div>
                        <h1 className="text-xl font-semibold">Acceso al reporte</h1>
                        <p className="text-sm text-muted-foreground mt-1">Ingresa el PIN de 4 dígitos que aparece en tu recibo</p>
                    </div>
                    {meta && (
                        <div className="text-sm rounded-lg border bg-secondary/40 p-3 mb-4 space-y-1">
                            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{meta.client_name}</span></div>
                            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" /><span className="font-mono">{meta.service_date}</span></div>
                        </div>
                    )}
                    <Label className="text-center block mb-2">PIN</Label>
                    <div className="flex justify-center mb-4">
                        <InputOTP maxLength={4} value={pin} onChange={setPin} onComplete={submit} data-testid="public-pin-input">
                            <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                            </InputOTPGroup>
                        </InputOTP>
                    </div>
                    <Button
                        className="w-full h-11 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                        onClick={() => submit()}
                        disabled={loading || pin.length !== 4}
                        data-testid="public-pin-submit-button"
                    >
                        {loading ? "Verificando..." : "Ingresar"}
                    </Button>
                </Card>
                <p className="text-xs text-muted-foreground mt-6">TRULYN, S.A. de C.V. • Tel: 2297-7600</p>
            </div>
        );
    }

    return <PublicReportDoc data={data} />;
}

export function PublicReportDoc({ data }) {
    const r = data.report;
    const c = data.client;
    const t = data.technician;
    return (
        <div className="max-w-3xl mx-auto p-6 bg-background min-h-screen" data-testid="public-report-view">
            <div className="flex items-center justify-between mb-4 no-print">
                <BrandLogo size={36} />
                <div className="flex gap-2">
                    <a href={assetUrl(r.report_pdf_url)} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid="public-download-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                    </a>
                </div>
            </div>
            <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Reporte de Servicio</h1>
                    <Badge variant="outline" className="font-mono">{r.id.slice(0,8)}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div><div className="text-xs text-muted-foreground">Cliente</div><div className="font-medium">{c.name}</div></div>
                    <div><div className="text-xs text-muted-foreground">Código</div><div className="font-mono">{c.client_code}</div></div>
                    <div><div className="text-xs text-muted-foreground">Técnico</div><div className="font-medium">{t.full_name}</div></div>
                    <div><div className="text-xs text-muted-foreground">Fecha</div><div className="font-mono">{r.service_date}</div></div>
                </div>
                {r.recommendations && (
                    <div className="mb-2 text-sm"><span className="font-semibold">Recomendaciones:</span> {r.recommendations}</div>
                )}
                {r.areas_treated && (
                    <div className="mb-2 text-sm"><span className="font-semibold">Áreas tratadas:</span> {r.areas_treated}</div>
                )}
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Recibido por: <span className="font-medium text-foreground">{r.accepted_by_name || "—"}</span></span>
                    <a href={assetUrl(r.report_pdf_url)} target="_blank" rel="noreferrer">
                        <Button className="bg-[hsl(var(--trulyn-navy))] text-white hover:bg-[hsl(var(--trulyn-navy))]/90">
                            <FileText className="w-4 h-4 mr-2" /> Ver reporte completo (PDF)
                        </Button>
                    </a>
                </div>
            </Card>
        </div>
    );
}
